<script>
/**
 * @component LiquidationSubmissionNew
 * @description UC-REG-169 — Crear nuevo submission del prestador (flujo B).
 *              2 tabs: **Carga manual** (grid editable línea a línea) e
 *              **Importar archivo** (placeholder por ahora — Fase 3.5).
 *              Al submit → POST /liquidation-submissions → redirige al
 *              detalle para ver progreso del procesamiento.
 * @example <LiquidationSubmissionNew />
 */
import submissionSvc from "@/services/liquidation-submission";
import userSvc from "@/services/user";
import debounce from "debounce";

const DEBOUNCE_MS = 400;
const MONTHS = [
  { v: 1, l: "Enero" },
  { v: 2, l: "Febrero" },
  { v: 3, l: "Marzo" },
  { v: 4, l: "Abril" },
  { v: 5, l: "Mayo" },
  { v: 6, l: "Junio" },
  { v: 7, l: "Julio" },
  { v: 8, l: "Agosto" },
  { v: 9, l: "Septiembre" },
  { v: 10, l: "Octubre" },
  { v: 11, l: "Noviembre" },
  { v: 12, l: "Diciembre" },
];

/**
 * @returns {Object} Línea vacía para el grid.
 * UC-REG-171 Fase A — DNI y Nº afiliado por separado; código nomenclador
 * y código según prestador por separado; denominación libre. Los 3 nuevos
 * (dni, memberNumber, providerServiceCode) son opcionales — al enviar,
 * al menos uno de {dni, memberNumber} debe estar completo.
 */
const emptyLine = () => ({
  // UC-REG-231 — TRX declarado por el prestador (identifica la SR exacta).
  declaredTransactionCode: "",
  declaredDni: "",
  declaredMemberNumber: "",
  declaredCode: "",
  providerServiceCode: "",
  declaredName: "",
  quantity: 1,
  performedAt: "",
  declaredAmount: null,
  authorizationCode: "",
});

/**
 * @description Campos internos del submission (ourFields) mostrados en la
 *   UI del importer como opciones del dropdown de mapping.
 *   El orden importa — los core arriba, los informativos abajo.
 */
const OUR_FIELDS_UI = [
  // UC-REG-231 — TRX declarado: la clave más confiable para matchear la SR
  //   existente (match exacto por transactionCode antes que por atributos).
  {
    key: "declaredTransactionCode",
    label: "TRX / N° transacción",
    required: false,
  },
  { key: "declaredCode", label: "Código nomenclador", required: true },
  {
    key: "providerServiceCode",
    label: "Código según prestador",
    required: false,
  },
  { key: "declaredName", label: "Denominación prestación", required: false },
  // UC-REG-171 Fase A — DNI y Nº afiliado por separado (uno obligatorio)
  { key: "declaredDni", label: "DNI", required: false },
  { key: "declaredMemberNumber", label: "Nº afiliado", required: false },
  // UC-REG-174 — Nombre del paciente separado de la denominación.
  { key: "patientFullName", label: "Nombre paciente", required: false },
  { key: "performedAt", label: "Fecha", required: true },
  { key: "declaredAmount", label: "Monto declarado", required: false },
  { key: "quantity", label: "Cantidad", required: false },
  { key: "authorizationCode", label: "Nº autorización", required: false },
  { key: "declaredPlan", label: "Plan", required: false },
  { key: "activityType", label: "Tipo actividad", required: false },
  { key: "providerReceiptId", label: "Nº comprobante", required: false },
  { key: "providerInvoiceNumber", label: "Nº factura", required: false },
  { key: "effectorName", label: "Efector (nombre)", required: false },
  { key: "effectorLicense", label: "Efector (matrícula)", required: false },
  { key: "branch", label: "Sucursal / sede", required: false },
  { key: "taxCondition", label: "Condición IVA", required: false },
];

export default {
  name: "LiquidationSubmissionNew",
  data() {
    return {
      tab: "manual",
      provider: "",
      providerOptions: [],
      providerSearchLoading: false,
      billingYear: new Date().getFullYear(),
      billingMonth: new Date().getMonth() + 1,
      months: MONTHS,
      years: this.buildYears(),
      lines: [emptyLine()],
      submitting: false,
      notes: "",
      // UC-REG-171 iter 3 — estado del importer
      importFile: null,
      importPreview: null, // { headers, sampleRows, detectedMapping, ... }
      importMapping: {}, // ourField → excelColumn (editable)
      importLoading: false,
      aiLoading: false,
      aiNotes: null,
      ourFieldsUI: OUR_FIELDS_UI,
    };
  },
  computed: {
    /** @returns {number} Total declarado en el submission */
    totalDeclared() {
      return this.lines.reduce(
        (sum, l) => sum + (Number(l.declaredAmount) || 0),
        0
      );
    },
    canSubmit() {
      if (!this.provider) return false;
      if (!this.billingYear || !this.billingMonth) return false;
      // UC-REG-171 Fase A — línea válida requiere código nomenclador +
      // fecha + al menos uno de {DNI, Nº afiliado}.
      const valid = this.lines.filter(
        (l) =>
          l.declaredCode &&
          (l.declaredDni || l.declaredMemberNumber) &&
          l.performedAt
      );
      return valid.length > 0;
    },
    // ─── Importer computeds (UC-REG-171 iter 3.1) ──────────────────
    /**
     * @returns {Map<string, {chosen, score}>} column → info del backend
     *   para lookup rápido en la UI (chip de confianza, etc).
     */
    importPerColumnMap() {
      if (!this.importPreview) return new Map();
      return new Map(
        (this.importPreview.perColumn || []).map((pc) => [pc.column, pc])
      );
    },
    /**
     * @returns {string[]} Ourfields obligatorios sin mapear.
     * UC-REG-171 Fase A — además de los required fijos, exige AL MENOS
     * uno de {DNI, Nº afiliado}. Si ninguno está mapeado, aparece en la lista.
     */
    missingRequiredFields() {
      if (!this.importPreview) return [];
      const strictMissing = this.ourFieldsUI
        .filter((f) => f.required && !this.importMapping[f.key])
        .map((f) => f.label);
      const hasMemberId =
        !!this.importMapping.declaredDni ||
        !!this.importMapping.declaredMemberNumber;
      if (!hasMemberId) strictMissing.push("DNI o Nº afiliado");
      return strictMissing;
    },
    /**
     * @returns {string[]} Columnas del archivo NO asignadas a ningún ourField.
     *   Estas van al `rawRow` del submission — se preservan pero no se usan
     *   en el pipeline.
     */
    importUnusedColumns() {
      if (!this.importPreview) return [];
      const usedCols = new Set(
        Object.values(this.importMapping).filter(Boolean)
      );
      return (this.importPreview.headers || []).filter((h) => !usedCols.has(h));
    },
    /**
     * @returns {Object} Contadores para el banner de estado
     */
    importStatus() {
      if (!this.importPreview) {
        return { mapped: 0, unmapped: 0, missingReq: 0, lowConfidence: 0 };
      }
      const mappedFields = Object.keys(this.importMapping).length;
      const lowConf = Object.values(this.importMapping).filter((col) => {
        const info = this.importPerColumnMap.get(col);
        return info && info.score < 0.7;
      }).length;
      return {
        mapped: mappedFields,
        unmapped: this.importUnusedColumns.length,
        missingReq: this.missingRequiredFields.length,
        lowConfidence: lowConf,
      };
    },
  },
  methods: {
    buildYears() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2, y - 3];
    },
    /**
     * @method searchProviders — Users con rol Doctor (donde apunta
     *   MedicalContract.provider). Incluye tanto prestadores
     *   individuales como institucionales (sanatorios cargados como User).
     */
    searchProviders: debounce(async function (query) {
      this.providerSearchLoading = true;
      try {
        const res = await userSvc.getDoctors({
          searchInput: query || "",
          limit: 20,
        });
        this.providerOptions = (res.docs || []).map((u) => ({
          value: u._id,
          label:
            `${u.lastName || ""}, ${u.firstName || ""}`
              .replace(/^, /, "")
              .trim() ||
            u.username ||
            "—",
        }));
      } finally {
        this.providerSearchLoading = false;
      }
    }, DEBOUNCE_MS),
    /** @method addLine */
    addLine() {
      this.lines.push(emptyLine());
    },
    /** @method removeLine */
    removeLine(idx) {
      this.lines.splice(idx, 1);
      if (this.lines.length === 0) this.lines.push(emptyLine());
    },
    /** @method formatMoney */
    formatMoney(n) {
      if (n == null) return "-";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },
    /** @method submit - Crea submission y redirige al detalle */
    async submit() {
      if (!this.canSubmit) {
        this.$message.warning(
          "Completá prestador, período y al menos una línea válida"
        );
        return;
      }
      this.submitting = true;
      try {
        // UC-REG-171 Fase A — línea válida requiere código + fecha +
        // uno de {DNI, memberNumber}. Se manda declaredMemberIdentifier
        // como fallback (DNI o memberNumber, en ese orden) para trazabilidad.
        const validLines = this.lines
          .filter(
            (l) =>
              l.declaredCode &&
              (l.declaredDni || l.declaredMemberNumber) &&
              l.performedAt
          )
          .map((l) => {
            const dni = l.declaredDni ? String(l.declaredDni).trim() : null;
            const memberNumber = l.declaredMemberNumber
              ? String(l.declaredMemberNumber).trim()
              : null;
            return {
              // UC-REG-231 — preservar el TRX declarado (antes el whitelist
              //   lo tiraba → el match por TRX no corría y colisionaba).
              declaredTransactionCode: l.declaredTransactionCode
                ? String(l.declaredTransactionCode).trim()
                : null,
              declaredCode: String(l.declaredCode).trim(),
              providerServiceCode: l.providerServiceCode
                ? String(l.providerServiceCode).trim()
                : null,
              declaredName: l.declaredName
                ? String(l.declaredName).trim()
                : null,
              declaredDni: dni,
              declaredMemberNumber: memberNumber,
              declaredMemberIdentifier: dni || memberNumber || null,
              quantity: Number(l.quantity) || 1,
              performedAt: l.performedAt,
              declaredAmount:
                l.declaredAmount != null ? Number(l.declaredAmount) : null,
              authorizationCode: l.authorizationCode || null,
            };
          });

        const res = await submissionSvc.create({
          provider: this.provider,
          billingPeriod: { year: this.billingYear, month: this.billingMonth },
          sourceMode: "manual",
          lines: validLines,
          notes: this.notes || null,
        });

        this.$message.success("Submission creado — procesamiento en curso");
        this.$router.push({
          name: "liquidation-submission-detail",
          params: { id: res.submissionId },
        });
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.$message.error(
            "Este submission ya existe (mismo contenido para este prestador y período)"
          );
        } else {
          this.$message.error(
            err.response?.data?.error || "Error al crear submission"
          );
        }
      } finally {
        this.submitting = false;
      }
    },
    /** @method cancel */
    cancel() {
      this.$router.push({ name: "liquidation-submission-list" });
    },

    // ─── Importer (UC-REG-171 iter 3) ──────────────────────────────────

    /**
     * @method onImportFileSelected — dispara al elegir archivo en el
     *   drop-zone. Sube al preview del backend y muestra el mapping
     *   detectado por las capas heurísticas (+ template si existe).
     * @param {File} file
     */
    async onImportFileSelected(file) {
      if (!this.provider) {
        this.$message.warning("Elegí primero el prestador");
        return false;
      }
      this.importFile = file;
      this.importLoading = true;
      this.aiNotes = null;
      try {
        const preview = await submissionSvc.previewImport(file, {
          provider: this.provider,
        });
        this.importPreview = preview;
        this.importMapping = { ...(preview.detectedMapping || {}) };
        if (preview.templateFound) {
          this.$message.success(
            `Mapping reutilizado del template previo del prestador (${preview.headers.length} columnas)`
          );
        } else if (preview.overallConfidence >= 0.85) {
          this.$message.success(
            `Detección automática ${Math.round(
              preview.overallConfidence * 100
            )}% — revisá y confirmá`
          );
        } else {
          this.$message.info(
            `Detección parcial ${Math.round(
              preview.overallConfidence * 100
            )}% — completá los campos faltantes o probá "Sugerir con IA"`
          );
        }
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al procesar el archivo"
        );
        this.importFile = null;
        this.importPreview = null;
      } finally {
        this.importLoading = false;
      }
      return false; // prevenir upload nativo de el-upload
    },

    /**
     * @method clearImport — resetea el estado del importer
     */
    clearImport() {
      this.importFile = null;
      this.importPreview = null;
      this.importMapping = {};
      this.aiNotes = null;
    },

    /**
     * @method onMappingChange — cuando el operador cambia el mapping
     *   de un campo interno, resolver conflictos: si otra columna estaba
     *   asignada a otro ourField que ahora se está usando, deja el nuevo.
     */
    onMappingChange(ourField, excelColumn) {
      if (!excelColumn) {
        this.$delete(this.importMapping, ourField);
        return;
      }
      // Si esa columna estaba asignada a otro field, deassignar el anterior
      Object.keys(this.importMapping).forEach((k) => {
        if (k !== ourField && this.importMapping[k] === excelColumn) {
          this.$delete(this.importMapping, k);
        }
      });
      this.$set(this.importMapping, ourField, excelColumn);
    },

    /**
     * @method suggestWithAI — dispara el endpoint de IA para llenar
     *   campos que la heurística no pudo mapear.
     */
    async suggestWithAI() {
      if (!this.importPreview) return;
      this.aiLoading = true;
      this.aiNotes = null;
      try {
        const unmapped = this.importPreview.headers.filter(
          (h) => !Object.values(this.importMapping).includes(h)
        );
        const res = await submissionSvc.resolveWithAI({
          headers: this.importPreview.headers,
          sampleRows: this.importPreview.sampleRows,
          existingMapping: this.importMapping,
          unmappedColumns: unmapped,
        });
        this.importMapping = { ...res.mapping };
        this.aiNotes = res.notes || null;
        const added = Object.keys(res.aiOnlyMapping || {}).length;
        this.$message.success(
          `IA agregó ${added} sugerencia${added === 1 ? "" : "s"} (${
            res.model
          })`
        );
      } catch (err) {
        const code = err.response?.data?.error;
        if (code === "AiImportDisabled") {
          this.$message.warning(
            "IA no configurada. Andá a Config → AI de importación para activarla."
          );
        } else {
          this.$message.error(
            err.response?.data?.message || "Error al consultar la IA"
          );
        }
      } finally {
        this.aiLoading = false;
      }
    },

    /**
     * @method mappingValue — get value de un ourField en el mapping
     */
    mappingValue(ourField) {
      return this.importMapping[ourField] || "";
    },

    /**
     * @method fieldConfidence — devuelve el score numérico de un ourField
     *   (0 si no está mapeado o el operador lo eligió manual).
     */
    fieldConfidence(ourField) {
      const col = this.importMapping[ourField];
      if (!col) return null;
      const info = this.importPerColumnMap.get(col);
      if (!info) return 0; // mapeado manual, sin score del detector
      return info.score || 0;
    },

    /**
     * @method fieldConfidenceLevel — 'high' | 'mid' | 'low' | 'manual' | 'empty' | 'none'
     */
    fieldConfidenceLevel(ourField, required) {
      const col = this.importMapping[ourField];
      if (!col) return required ? "empty" : "none";
      const score = this.fieldConfidence(ourField);
      if (score === 0) return "manual";
      if (score >= 0.85) return "high";
      if (score >= 0.55) return "mid";
      return "low";
    },

    /**
     * @method fieldConfidencePct — porcentaje redondeado (0-100) o null
     */
    fieldConfidencePct(ourField) {
      const s = this.fieldConfidence(ourField);
      return s == null ? null : Math.round(s * 100);
    },

    /**
     * @method rowClasses — clases del <tr> del mapping-grid
     */
    rowClasses(f) {
      const level = this.fieldConfidenceLevel(f.key, f.required);
      return {
        "is-required": f.required,
        "is-unmapped": level === "empty",
        "is-lowconf": level === "low" || level === "mid",
      };
    },

    /**
     * @method confidenceLabel — texto del chip de confianza
     */
    confidenceLabel(f) {
      const level = this.fieldConfidenceLevel(f.key, f.required);
      const pct = this.fieldConfidencePct(f.key);
      if (level === "empty") return "Sin mapear";
      if (level === "none") return "—";
      if (level === "manual") return "Manual";
      if (level === "high") return `Alta ${pct}%`;
      if (level === "mid") return `Media ${pct}%`;
      if (level === "low") return `Baja ${pct}%`;
      return "";
    },

    /**
     * @method previewCellValue — valor formateado del sample para una celda
     */
    previewCellValue(row, ourField) {
      const col = this.importMapping[ourField];
      if (!col) return "—";
      const v = row[col];
      if (v == null || v === "") return "—";
      if (v instanceof Date) return v.toLocaleDateString("es-AR");
      return String(v);
    },

    /**
     * @method canConfirmImport — habilita el submit del importer cuando
     *   los campos obligatorios estrictos están mapeados + AL MENOS uno
     *   de {DNI, Nº afiliado} (UC-REG-171 Fase A).
     */
    canConfirmImport() {
      const req = OUR_FIELDS_UI.filter((f) => f.required).map((f) => f.key);
      const strictOk = req.every((k) => !!this.importMapping[k]);
      const hasMemberId =
        !!this.importMapping.declaredDni ||
        !!this.importMapping.declaredMemberNumber;
      return strictOk && hasMemberId;
    },

    /**
     * @method confirmImport — guarda el template + crea el submission con
     *   las líneas transformadas.
     */
    async confirmImport() {
      if (!this.canConfirmImport()) {
        this.$message.warning(
          "Faltan mapear campos obligatorios (código, afiliado, fecha)"
        );
        return;
      }
      this.submitting = true;
      try {
        // 1) Guardar template (aprende sinónimos también)
        try {
          await submissionSvc.saveTemplate({
            provider: this.provider,
            fingerprint: this.importPreview.fingerprint,
            headers: this.importPreview.headers,
            columnMapping: this.importMapping,
            fileTypeHint: this.importFile
              ? this.importFile.name.split(".").pop().toLowerCase()
              : null,
            sheetName: this.importPreview.fileMeta?.sheetName,
            headerRowIndex: this.importPreview.fileMeta?.headerRowIndex,
          });
        } catch (tErr) {
          // UC-REG-188 — El template es no-bloqueante para el submission,
          //   pero el error concreto se muestra en toast para diagnóstico.
          //   Antes solo hacía console.warn silencioso.
          const msg =
            tErr.response?.data?.error ||
            tErr.response?.data?.message ||
            tErr.message;
          this.$message.warning(
            `Template no se guardó (submission igual creado): ${msg}`
          );
          console.warn("saveTemplate failed", tErr);
        }

        // 2) Transformar rows completas (no solo las 5 del preview)
        //    Rehacer subida para obtener TODAS las rows transformadas.
        const previewFull = await submissionSvc.previewImport(this.importFile, {
          provider: this.provider,
        });
        // Aplicar el mapping confirmado (puede diferir del detectado)
        const allLines = (previewFull.sampleRows || []).length
          ? this.buildLinesFromRows(
              previewFull.sampleRows,
              previewFull.totalRows
            )
          : [];

        if (!allLines.length) {
          this.$message.error("El archivo no tiene líneas para procesar");
          this.submitting = false;
          return;
        }

        // 3) Crear submission con sourceMode='imported'
        const res = await submissionSvc.create({
          provider: this.provider,
          billingPeriod: {
            year: this.billingYear,
            month: this.billingMonth,
          },
          sourceMode: "imported",
          importFormat: this.importFile
            ? this.importFile.name.split(".").pop().toLowerCase()
            : null,
          lines: allLines,
          notes: this.notes || null,
        });
        this.$message.success(
          `Submission importado — ${allLines.length} líneas, procesamiento en curso`
        );
        this.$router.push({
          name: "liquidation-submission-detail",
          params: { id: res.submissionId },
        });
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.$message.error(
            "Este submission ya existe (mismo contenido para prestador y período)"
          );
        } else {
          this.$message.error(
            err.response?.data?.error || "Error al procesar la importación"
          );
        }
      } finally {
        this.submitting = false;
      }
    },

    /**
     * @method buildLinesFromRows — mapea rows completas del preview al
     *   modelo de submission usando el mapping confirmado. Aplica las
     *   mismas normalizaciones que el backend (fecha, monto, cantidad).
     */
    buildLinesFromRows(rows) {
      return rows.map((r, idx) => {
        const line = { lineNumber: idx + 1 };
        Object.entries(this.importMapping).forEach(([ourField, col]) => {
          const v = r[col];
          if (v == null || v === "") return;
          if (ourField === "quantity") {
            line[ourField] = Number(v) || 1;
          } else if (ourField === "declaredAmount") {
            const num =
              typeof v === "number"
                ? v
                : Number(String(v).replace(/[$\s]/g, "").replace(",", "."));
            line[ourField] = Number.isFinite(num) ? num : null;
          } else if (ourField === "performedAt") {
            line[ourField] = this.normalizeDate(v);
          } else {
            line[ourField] = String(v).trim();
          }
        });
        // UC-REG-171 Fase A — sintetizar declaredMemberIdentifier legacy
        // desde DNI o memberNumber para el matcher del backend.
        if (!line.declaredMemberIdentifier) {
          line.declaredMemberIdentifier =
            line.declaredDni || line.declaredMemberNumber || null;
        }
        return line;
      });
    },

    /** @method normalizeDate — mismo criterio que el backend (dd/mm/yyyy AR) */
    normalizeDate(v) {
      if (!v) return "";
      const s = String(v).trim();
      const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (slash) {
        const [, a, b, y] = slash;
        const na = Number(a);
        const nb = Number(b);
        let d;
        let m;
        if (na > 12 && nb <= 12) {
          d = a;
          m = b;
        } else if (nb > 12 && na <= 12) {
          m = a;
          d = b;
        } else {
          d = a;
          m = b;
        }
        const yr = y.length === 2 ? Number(y) + 2000 : Number(y);
        return `${yr}-${String(m).padStart(2, "0")}-${String(d).padStart(
          2,
          "0"
        )}`;
      }
      const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (iso) {
        const [, y, m, d] = iso;
        return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(
          2,
          "0"
        )}`;
      }
      return s;
    },
  },
};
</script>

<template lang="pug">
.subm-new
  //- Header
  .subm-new__head
    .subm-new__title Nueva liquidación
    .subm-new__actions
      el-button(size="small" @click="cancel") Cancelar
      el-button(
        v-if="tab === 'manual'"
        type="primary" size="small"
        :loading="submitting" :disabled="!canSubmit"
        @click="submit"
      ) Procesar

  //- Form top: prestador + período
  .subm-new__topform
    .subm-new__field
      label.form-label Prestador
      el-select(
        v-model="provider"
        filterable remote reserve-keyword
        placeholder="Buscar prestador…"
        :remote-method="searchProviders"
        :loading="providerSearchLoading"
        @visible-change="v => v && !providerOptions.length && searchProviders('')"
        style="width:100%"
      )
        el-option(v-for="o in providerOptions" :key="o.value" :label="o.label" :value="o.value")
    .subm-new__field
      label.form-label Período de facturación
      .subm-new__period
        el-select(v-model="billingMonth" style="flex:1")
          el-option(v-for="m in months" :key="m.v" :label="m.l" :value="m.v")
        el-select(v-model="billingYear" style="flex:1")
          el-option(v-for="y in years" :key="y" :label="String(y)" :value="y")

  //- Tabs manual / importer
  .subm-new__tabs
    .subm-new__tab(
      :class="{ 'is-active': tab === 'manual' }"
      @click="tab = 'manual'"
    ) Carga manual
    .subm-new__tab(
      :class="{ 'is-active': tab === 'imported' }"
      @click="tab = 'imported'"
    ) Importar archivo

  //- Tab: Manual — grid editable (UC-REG-171 Fase A: 10 columnas)
  .subm-new__grid(v-if="tab === 'manual'")
    .subm-new__gridwrap
      table
        thead
          tr
            th.n #
            th.dni DNI
            th.afil Nº afiliado
            th.code Cód. nomenclador
            th.pcode Cód. prestador
            th.desc Denominación
            th.qty Cant.
            th.date Fecha
            th.amt Valor decl.
            th.auth Nº AUTH
            th.acts
        tbody
          tr(v-for="(line, idx) in lines" :key="idx")
            td.n {{ idx + 1 }}
            td.dni
              el-input(
                v-model="line.declaredDni"
                size="small" placeholder="DNI"
              )
            td.afil
              el-input(
                v-model="line.declaredMemberNumber"
                size="small" placeholder="Nº afiliado"
              )
            td.code
              el-input(
                v-model="line.declaredCode"
                size="small" placeholder="Nomenclador"
              )
            td.pcode
              el-input(
                v-model="line.providerServiceCode"
                size="small" placeholder="—"
              )
            td.desc
              el-input(
                v-model="line.declaredName"
                size="small" placeholder="—"
              )
            td.qty
              el-input-number(
                v-model="line.quantity" :min="1" size="small" controls-position="right"
              )
            td.date
              el-input(
                v-model="line.performedAt"
                size="small" type="date"
              )
            td.amt
              el-input(
                v-model="line.declaredAmount"
                size="small" type="number" placeholder="0"
              )
            td.auth
              el-input(
                v-model="line.authorizationCode"
                size="small" placeholder="—"
              )
            td.acts
              el-button(
                type="danger" plain size="mini" icon="el-icon-delete"
                @click="removeLine(idx)"
              )
    .subm-new__gridfooter
      el-button(icon="el-icon-plus" size="small" @click="addLine") Agregar línea
      .subm-new__gridtotals
        span.muted {{ lines.length }} línea{{ lines.length === 1 ? '' : 's' }}
        span.muted ·
        span DNI o Nº afiliado obligatorio ·
        span Total declarado:
        span.total {{ formatMoney(totalDeclared) }}

  //- Tab: Importer (UC-REG-171 iter 3)
  .subm-new__importer(v-if="tab === 'imported'")
    //- Drop-zone (visible cuando no hay archivo cargado)
    el-upload.uploader(
      v-if="!importPreview"
      drag
      action=""
      :auto-upload="false"
      :on-change="(f) => onImportFileSelected(f.raw)"
      :show-file-list="false"
      :disabled="importLoading || !provider"
      accept=".xlsx,.xls,.csv,.tsv"
    )
      i.el-icon-upload
      .el-upload__text(v-if="!importLoading")
        | Arrastrá el archivo o
        em  hacé click
        |  para elegir
      .el-upload__text(v-else) Procesando archivo…
      .el-upload__tip(slot="tip")
        span(v-if="!provider" style="color:#dc2626") Primero elegí el prestador arriba
        span(v-else) Excel (.xlsx, .xls), CSV o TSV — máx 20 MB

    //- Preview + tabla de mapping (visible tras subir)
    template(v-if="importPreview")
      .import-summary
        .import-summary__file
          i.el-icon-document
          span {{ importPreview.fileMeta.name }}
          span.muted · {{ importPreview.totalRows }} línea{{ importPreview.totalRows === 1 ? '' : 's' }}
          span.muted · sheet "{{ importPreview.fileMeta.sheetName }}"
        .import-summary__actions
          el-button(size="mini" icon="el-icon-refresh-right" @click="clearImport") Cambiar archivo
          el-button(
            size="mini"
            type="primary" plain
            icon="el-icon-magic-stick"
            :loading="aiLoading"
            @click="suggestWithAI"
          ) Sugerir con IA

      //- Banner de estado operativo (reemplaza el % genérico)
      .import-status(
        :class="{ 'import-status--error': importStatus.missingReq > 0, 'import-status--warn': importStatus.missingReq === 0 && (importStatus.unmapped > 0 || importStatus.lowConfidence > 0), 'import-status--ok': importStatus.missingReq === 0 && importStatus.unmapped === 0 && importStatus.lowConfidence === 0 }"
      )
        .import-status__row
          span.import-status__chip.is-ok
            i.el-icon-circle-check
            | &nbsp;{{ importStatus.mapped }} campo{{ importStatus.mapped === 1 ? '' : 's' }} mapeado{{ importStatus.mapped === 1 ? '' : 's' }}
          span.import-status__chip.is-warn(v-if="importStatus.lowConfidence > 0")
            i.el-icon-warning-outline
            | &nbsp;{{ importStatus.lowConfidence }} con confianza baja
          span.import-status__chip.is-warn(v-if="importStatus.unmapped > 0")
            i.el-icon-info
            | &nbsp;{{ importStatus.unmapped }} columna{{ importStatus.unmapped === 1 ? '' : 's' }} del archivo sin usar
          span.import-status__chip.is-error(v-if="importStatus.missingReq > 0")
            i.el-icon-error
            | &nbsp;Faltan obligatorios: {{ missingRequiredFields.join(', ') }}
        .import-status__note(v-if="importPreview.templateFound")
          i.el-icon-collection-tag
          | &nbsp;Mapping reutilizado del template previo del prestador.

      .ai-notes(v-if="aiNotes")
        strong IA:&nbsp;
        span {{ aiNotes }}

      .mapping-grid
        .mapping-grid__title Mapeo de columnas
        table
          thead
            tr
              th Campo interno
              th Columna del archivo
              th.conf Confianza
              th Muestra
          tbody
            tr(
              v-for="f in ourFieldsUI"
              :key="f.key"
              :class="rowClasses(f)"
            )
              td
                span {{ f.label }}
                span.req(v-if="f.required") *
              td
                el-select(
                  :value="mappingValue(f.key)"
                  @change="(v) => onMappingChange(f.key, v)"
                  size="small"
                  clearable
                  placeholder="— no mapear —"
                  style="width:100%"
                )
                  el-option(
                    v-for="col in importPreview.headers"
                    :key="col"
                    :label="col"
                    :value="col"
                  )
              td.conf
                span(:class="'conf-chip conf-chip--' + fieldConfidenceLevel(f.key, f.required)")
                  | {{ confidenceLabel(f) }}
              td.sample
                span(v-if="importPreview.sampleRows[0]")
                  | {{ previewCellValue(importPreview.sampleRows[0], f.key) }}
                span.muted(v-else) —

      .mapping-preview(v-if="importPreview.sampleRows.length")
        .mapping-preview__title Preview (primeras 5 líneas del archivo)
        .mapping-preview__table
          table
            thead
              tr
                th Nº
                th(v-for="f in ourFieldsUI.filter(x => importMapping[x.key])" :key="f.key") {{ f.label }}
            tbody
              tr(v-for="(row, idx) in importPreview.sampleRows.slice(0,5)" :key="idx")
                td.muted {{ idx + 1 }}
                td(v-for="f in ourFieldsUI.filter(x => importMapping[x.key])" :key="f.key")
                  | {{ previewCellValue(row, f.key) }}

      .import-confirm
        el-button(
          type="primary"
          :disabled="!canConfirmImport()"
          :loading="submitting"
          @click="confirmImport"
        ) Confirmar y procesar {{ importPreview.totalRows }} líneas
        span.import-confirm__hint(v-if="!canConfirmImport()")
          | Faltan campos obligatorios (*): código, afiliado, fecha

  //- Notas
  .subm-new__notes
    label.form-label Notas (opcional)
    el-input(
      v-model="notes"
      type="textarea" :rows="2" size="small"
      placeholder="Observaciones internas del operador"
    )
</template>

<style lang="scss" scoped>
.subm-new {
  font-family: "Plus Jakarta Sans", sans-serif;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
}
.subm-new__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.subm-new__title {
  font-size: 18px;
  font-weight: 500;
  color: #002d5e;
}

.subm-new__topform {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}
.subm-new__period {
  display: flex;
  gap: 8px;
}
.form-label {
  display: block;
  font-size: 12px;
  color: #6b7294;
  font-weight: 500;
  margin-bottom: 6px;
}

.subm-new__tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 16px;
}
.subm-new__tab {
  padding: 10px 20px;
  font-size: 13px;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  font-weight: 500;
  &.is-active {
    color: #002d5e;
    border-bottom-color: #002d5e;
  }
}

.subm-new__grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  .subm-new__gridwrap {
    overflow-x: auto;
  }
  table {
    // UC-REG-171 Fase A — 10 columnas fuerzan min-width para que scroll
    // horizontal aparezca sin comprimir inputs.
    min-width: 1180px;
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  th {
    text-align: left;
    padding: 9px 10px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 500;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  th.n {
    width: 32px;
  }
  th.dni {
    width: 108px;
  }
  th.afil {
    width: 118px;
  }
  th.code {
    width: 130px;
  }
  th.pcode {
    width: 120px;
  }
  th.desc {
    min-width: 180px;
  }
  th.qty {
    width: 82px;
  }
  th.date {
    width: 138px;
  }
  th.amt {
    text-align: right;
    width: 118px;
  }
  th.auth {
    width: 108px;
  }
  th.acts {
    width: 46px;
  }
  td {
    padding: 6px 8px;
    border-bottom: 0.5px solid #f1f5f9;
    vertical-align: middle;
  }
  td.n {
    color: #94a3b8;
    text-align: center;
  }
  td.qty {
    text-align: right;
  }
  td.amt {
    text-align: right;
  }
  td.acts {
    text-align: center;
  }
}
.subm-new__gridfooter {
  display: flex;
  align-items: center;
  padding: 12px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
}
.subm-new__gridtotals {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #475569;
}
.subm-new__gridtotals .total {
  font-weight: 600;
  color: #002d5e;
  font-variant-numeric: tabular-nums;
}
.muted {
  color: #94a3b8;
}

// UC-REG-171 iter 3 — Importer UI
.subm-new__importer {
  .uploader {
    display: block;
    ::v-deep .el-upload {
      width: 100%;
      .el-upload-dragger {
        width: 100%;
        height: 180px;
      }
    }
  }
}
.import-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
  &__file {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #1a1f36;
    i {
      color: #002d5e;
      font-size: 16px;
    }
  }
  &__actions {
    margin-left: auto;
    display: flex;
    gap: 6px;
  }
  .muted {
    color: #94a3b8;
  }
}
.import-hint {
  padding: 10px 14px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  color: #065f46;
  font-size: 12px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  &--warn {
    background: #fef3c7;
    border-color: #fde68a;
    color: #92400e;
  }
}

.import-status {
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  margin-bottom: 12px;

  &--ok {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
  &--warn {
    background: #fef3c7;
    border-color: #fde68a;
  }
  &--error {
    background: #fef2f2;
    border-color: #fecaca;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    background: white;
    border: 1px solid #e2e8f0;
    color: #475569;
    i {
      font-size: 13px;
    }
    &.is-ok {
      color: #065f46;
      border-color: #a7f3d0;
      background: #ecfdf5;
    }
    &.is-warn {
      color: #92400e;
      border-color: #fde68a;
      background: #fef3c7;
    }
    &.is-error {
      color: #991b1b;
      border-color: #fecaca;
      background: #fef2f2;
    }
  }

  &__note {
    margin-top: 8px;
    font-size: 11.5px;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 6px;
    i {
      color: #002d5e;
    }
  }
}

.conf-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  font-variant-numeric: tabular-nums;
  &--high {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }
  &--mid {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  }
  &--low {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
  &--manual {
    background: #ede9fe;
    color: #5b21b6;
    border: 1px solid #ddd6fe;
  }
  &--empty {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
  &--none {
    color: #94a3b8;
  }
}
.ai-notes {
  padding: 10px 14px;
  background: #ede9fe;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  color: #5b21b6;
  font-size: 12px;
  margin-bottom: 12px;
  line-height: 1.5;
}
.mapping-grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  &__title {
    padding: 10px 14px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 600;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }
  th {
    text-align: left;
    padding: 8px 12px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 500;
    background: #fafcff;
    border-bottom: 0.5px solid #e2e8f0;
  }
  td {
    padding: 8px 12px;
    border-bottom: 0.5px solid #f1f5f9;
    color: #1a1f36;
    vertical-align: middle;
  }
  td.sample {
    color: #64748b;
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 11px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  th.conf {
    width: 110px;
    text-align: center;
  }
  td.conf {
    text-align: center;
    white-space: nowrap;
  }
  .req {
    color: #dc2626;
    margin-left: 3px;
  }
  tr.is-unmapped {
    background: #fef2f2;
  }
  tr.is-lowconf {
    background: #fffbeb;
  }
  tr.is-required td:first-child {
    font-weight: 500;
  }
}
.mapping-preview {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;
  &__title {
    padding: 10px 14px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 600;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  &__table {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11.5px;
  }
  th {
    text-align: left;
    padding: 7px 10px;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
    font-weight: 500;
    background: #fafcff;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  td {
    padding: 7px 10px;
    border-bottom: 0.5px solid #f1f5f9;
    color: #1a1f36;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.import-confirm {
  display: flex;
  align-items: center;
  gap: 12px;
  &__hint {
    font-size: 12px;
    color: #dc2626;
  }
}

.subm-new__notes {
  margin-top: 20px;
}
</style>
